/* eslint-disable */
import { ChevronLeft } from 'lucide-react'
import { useEffect, useState, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Label } from '../../Components/ui/label'
import { Input2 } from '../../Components/ui/input2'
import { Button } from '../../Components/ui/button'
import { Card, CardContent } from '../../Components/ui/card'
import { useModal } from '../../hooks/use-modal-store'
import { useErrorPopup } from '../../hooks/useErrorPopup'
import PostHogTracker from '../../utils/PostHogTracker'
import env from '../../env/env'
import { post } from '../../http/restservice'
import { useRecoilState } from 'recoil'
import { userDetailsState } from '../LoginState'
import { formatIndianNumber } from '../../utils/Util'
import { toCurrency } from '../../utils/CurrencyUtil'
import OrderOtpVerification from '../FirstIdeas/OrderOtpVerification'
import { cn } from '../../lib/utils'
import basketRowIcon from '../../assets/SVGS/fabits-basket-row-icon.svg'

const PERCENTAGE_OPTIONS = [10, 25, 50, 75, 100]

const MFBasketRedeem = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { onOpen } = useModal()
  const { showError } = useErrorPopup()
  const [openOTPPopup, setOpenOTPPopup] = useState(false)
  const [basket] = useState(location?.state?.basket || [])
  const [userDetails] = useRecoilState(userDetailsState)
  const email = userDetails?.email
  const [redemptionPercentage, setRedemptionPercentage] = useState(100)
  const [redemptionAmount, setRedemptionAmount] = useState(0) // precise value (can have decimals)
  const [redemptionAmountDisplay, setRedemptionAmountDisplay] = useState('0') // shown in input (no decimals)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [redemptionFund, setRedemptionFund] = useState([])

  const clientCode = userDetails?.uid?.split('-')[0]

  // ---- Derived Values ----
  const totalCurrentAmount = useMemo(() => {
    return basket.reduce((acc, plan) => acc + plan.currentNav * plan.netUnits, 0).toFixed(3)
  }, [basket])

  useEffect(() => {
    const preciseAmount = Number(totalCurrentAmount) || 0
    setRedemptionAmount(preciseAmount)
    setRedemptionAmountDisplay(toCurrency(Math.floor(preciseAmount)) || '0')
  }, [totalCurrentAmount])

  // ---- Helpers ----
  const showPopup = (title, message, severity = 'info', admin = false) => {
    onOpen(admin ? 'openAdminPopup' : 'openPopup', {
      title,
      message,
      severity,
      redirectBackTo: '/dashboard/mutual-funds/investments',
      forwardTo: '/dashboard/mutual-funds',
    })
  }

  const handleRedemptionAmountChange = (e) => {
    setError('')
    const value = e.target.value.replace(/[^0-9]/g, '')
    const numericValue = Number(value) || 0

    setRedemptionAmountDisplay(toCurrency(numericValue) || '0')
    setRedemptionAmount(numericValue)
    setRedemptionPercentage(0)

    if (numericValue > Number(totalCurrentAmount)) {
      setError('Entered amount exceeds available balance')
    }
  }

  const handleRedemptionPercentage = (btn) => {
    setError('')
    setRedemptionPercentage(btn)
    const amount = Number(totalCurrentAmount) || 0

    const percentage = Number(btn)
    const calculatedAmount = (amount * percentage) / 100
    setRedemptionAmount(calculatedAmount)
    setRedemptionAmountDisplay(toCurrency(Math.floor(calculatedAmount)) || '0')
  }

  const buildRedemptionOrderFund = (fund) => {
    const base = {
      isin: fund.isin,
      folioNo: fund.folioNo,
      schemeCode: fund.schemeCode,
      schemeName: fund.schemeName,
      redemptionType: fund.redemptionType,
      nav: fund.nav,
    }

    if (fund.redemptionType === 'ALL_UNITS') {
      return { ...base, isAllRedeem: 'Y' }
    }

    if (fund.redemptionType === 'UNITS') {
      return {
        ...base,
        isAllRedeem: 'N',
        withdrawableUnits: fund.withdrawableUnits,
      }
    }

    return {
      ...base,
      isAllRedeem: 'N',
      withdrawableAmount: fund.withdrawableAmount,
    }
  }

  const showRedemptionBreakdown = (redemptionResponse) => {
    onOpen('openMFRedeemBreakdown', {
      constituents: redemptionResponse,
      amount: Math.round(Number(redemptionAmount)),
      onContinue: generateOTP,
    })
  }

  // ---- API Handlers ----
  const fetchRedemptionFunds = async () => {
    const redemptionUrl = `${env.API_URL}mutualfundservice/api/basket/redemptionFunds`
    const redemptionPayload = {
      customerBasketInvestmentId: basket[0]?.customerBasketInvestmentId,
      universalBasketId: basket[0]?.universalBasketId || 1,
      withdrawableAmount: Math.round(Number(redemptionAmount)),
      isV2Enabled: true,
    }

    const redemptionResponse = await post(redemptionUrl, redemptionPayload)

    if (redemptionResponse?.isError || !redemptionResponse?.length) {
      showError(
        'Redemption Failed!',
        redemptionResponse?.message ||
          'There was an issue with redemption. Please try again later. For assistance, contact us at +91-8050034800.',
        () => {},
      )
      return null
    }

    setRedemptionFund(redemptionResponse)
    return redemptionResponse
  }

  const placeBasketRedeemOrder = async (redemptionResponse) => {
    const orderData = redemptionResponse.map(buildRedemptionOrderFund)

    const orderPayload = {
      clientCode: clientCode?.toUpperCase(),
      phoneNumber: formatIndianNumber(userDetails?.phoneNumber),
      customerBasketInvestmentId: basket[0]?.customerBasketInvestmentId,
      orderData,
    }

    const basketRedeem = await post(
      `${env.API_URL}mutualfundservice/api/basket/oneTimeRedemptionOrder`,
      orderPayload,
    )

    if (basketRedeem?.isError || basketRedeem?.status === 'FAILURE') {
      showError(
        'Redemption Failed!',
        basketRedeem?.message ||
          'There was an issue with redemption. Please try again later. For assistance, contact us at +91-8050034800.',
        () => {},
      )
      return null
    }

    if (basketRedeem?.status === 'SUCCESS') {
      const successOrders = basketRedeem.orders?.filter((o) => o?.status === 'SUCCESS')
      if (successOrders?.length === basketRedeem.orders?.length) {
        showPopup(
          'Redemption Initiated!',
          'Your redemption request has been successfully initiated. Redemption will be completed in 1–5 working days.',
          'success',
        )
        return basketRedeem
      }
    }

    return basketRedeem
  }

  const handleBasketRedeem = async () => {
    setError('')

    if (Number(redemptionAmount) > Number(totalCurrentAmount)) {
      return setError('Entered amount exceeds available balance')
    }

    try {
      setIsLoading(true)

      const redemptionResponse = await fetchRedemptionFunds()
      if (!redemptionResponse) return

      showRedemptionBreakdown(redemptionResponse)
    } catch (err) {
      console.error('Redemption API Error:', err)
      showError(
        'Redemption Failed!',
        'There was an issue with redemption. Please try again later. For assistance, contact us at +91-8050034800.',
        () => {},
      )
    } finally {
      setIsLoading(false)
    }
  }

  const generateOTP = async () => {
    const response = await post(
      `${env.API_URL}mutualfundservice/api/mutualFund/sendTransactionalOtp`,
      {
        phoneNumber: userDetails?.phoneNumber,
        email: email.includes('fabits') ? '' : email,
      },
    )

    if (response.isError) {
      PostHogTracker.buttonClicked('Order OTP Resend Failed', {
        action: 'Order OTP Resend Failed',
        page_name: 'Action Center Orders Page',
      })
      showError('OTP Error', 'Failed to generate OTP. Please try again later.', () => {})
      throw new Error('Failed to generate OTP')
    }

    PostHogTracker.buttonClicked('Order OTP Resend Success', {
      action: 'Order OTP Resend Successful',
      page_name: 'Action Center Orders Page',
    })
    setOpenOTPPopup(true)
  }

  // ---- Render ----
  return (
    <>
      <div className='bg-white fabits-order-page'>
        <div className='max-w-[1128px] mx-auto min-h-screen pb-12 fabits-order-shell'>
          <div
            className='flex gap-x-1 items-center font-semibold cursor-pointer lg:py-2.5 p-4 lg:px-0 fabits-order-header'
            onClick={() => navigate(-1)}
          >
            <ChevronLeft className='size-5' />
            Redeem
          </div>

          <div className='max-w-[360px] mx-auto h-full flex flex-col gap-y-8 fabits-order-panel'>
            <div className='min-h-[170px] w-full flex justify-center items-center p-4 z-0 relative fabits-order-ticket'>
              <div className='flex flex-col items-center justify-center text-center z-10 fabits-order-ticket-content'>
                <img
                  src={basketRowIcon}
                  alt=''
                  aria-hidden='true'
                  className='fabits-basket-row-icon'
                />
                <h3 className='font-semibold text-sm leading-tight mt-3 text-pretty fabits-order-fund-name'>
                  {basket[0]?.customerBasketName}
                </h3>
                <div className='flex flex-col items-center justify-center mt-3'>
                  <span className='text-xs fabits-order-label'>Available Balance</span>
                  <span className='font-semibold text-2xl fabits-amount'>
                    ₹ {toCurrency(Math.floor(totalCurrentAmount))}
                  </span>
                </div>
              </div>
            </div>

            <div className='flex flex-col items-center gap-1.5 fabits-order-amount-block'>
              <Label htmlFor='amount' className='text-black-400 text-xs fabits-order-label'>
                Withdrawal Amount
              </Label>
              <Input2
                startAdornment='₹'
                type='text'
                id='amount'
                placeholder='Amount'
                className='border-none outline-none font-DMSans font-semibold text-3xl w-auto min-w-[100px] max-w-[360px] fabits-order-amount-input fabits-amount'
                style={{
                  width: `${Math.max(String(redemptionAmountDisplay).length * 24 + 80, 120)}px`,
                }}
                value={redemptionAmountDisplay}
                onChange={handleRedemptionAmountChange}
              />
              {error && <p className='text-xs text-error-600 text-center'>{error}</p>}
            </div>

            <div className='flex flex-wrap justify-center gap-2'>
              {PERCENTAGE_OPTIONS.map((btn) => (
                <Button
                  key={btn}
                  variant='simple'
                  size='simple'
                  className={cn(
                    'px-4 text-sm',
                    btn === redemptionPercentage
                      ? 'fabits-range-pill fabits-range-pill-active'
                      : 'fabits-range-pill',
                  )}
                  onClick={() => handleRedemptionPercentage(btn)}
                >
                  {btn === 100 ? 'Full' : `${btn}%`}
                </Button>
              ))}
            </div>

            <Card className='w-full fabits-order-info-card'>
              <CardContent className='p-4'>
                <h5 className='font-medium'>Important Notice</h5>
                <p className='text-sm text-black-300 mt-1'>
                  Withdrawn funds will be transferred to your linked bank account within 1-5
                  business days.
                </p>
              </CardContent>
            </Card>

            <Button
              className='w-full rounded-xl bg-primary-500 fabits-order-primary-action'
              loading={isLoading}
              onClick={handleBasketRedeem}
              disabled={
                isLoading ||
                Number(redemptionAmount) > Number(totalCurrentAmount) ||
                Number(redemptionAmount) <= 0
              }
            >
              Continue Withdrawal
            </Button>
          </div>
        </div>
      </div>

      <OrderOtpVerification
        phoneNumber={userDetails?.phoneNumber}
        openOTPPopup={openOTPPopup}
        setOpenOTPPopup={setOpenOTPPopup}
        email={userDetails?.email}
        handleSipOrOT={() => placeBasketRedeemOrder(redemptionFund)}
      />
    </>
  )
}

export default MFBasketRedeem
